import { RetryAgent } from "undici";
import Consumer from "./Consumer";
import Terminal from "./Terminal";
import { sortArrayByIntProperty, sortArrayByProperty, sortArrayByValueFunction } from "./utils";

import { type CableType } from "./CableType";
import type TerminalStrip from "./TerminalStrip";

import Logger from "../Logger";
import ELCB from "./ELCB";
import { create } from "domain";
const logger = new Logger();
export default class TerminalBlock {
  sortString = "";

  mainTerminal: Terminal;
  subTerminals: Array<Terminal>;

  constructor(mainTerminal: Terminal, subTerminals: Array<Terminal>) {
    this.mainTerminal = mainTerminal;
    this.subTerminals = subTerminals;
  }

  get fullTag(): string {
    if (this.subTerminals.length == 0) {
      return this.mainTerminal.tag;
    }
    let tagPrefixPart = this.mainTerminal.parent?.fullTag + ":";

    const size = this.size;
    const index = this.mainTerminal.tagIndex;

    switch (size) {
      case 1:
        return `${tagPrefixPart}${index}`;
        break;

      case 2:
        return `${tagPrefixPart}${index},${index + 1}`;
        break;

      default:
        return `${tagPrefixPart}${index}-${index + size - 1}`;
        break;
    }
  }

  //
  // "size" liefert die Anzahl der physialischen Klemmen dieses Klemmmblocks
  // Estada geht im Moment immer von Doppelstock Klemmen aus!!
  //
  // Das heißt für
  //  * 11,12 liefert size 2
  //  * 1-3 liefert size 3
  //  * 11, 12grau, 12schwarz liefert size 2

  get size(): number {
    if (this.subTerminals.length == 0) {
      return this.mainTerminal.size;
    }

    //
    // this should work,
    // because estada allways uses 2 story termials
    // and is more stable as to analyse the tags of the subTerminals
    //
    return Math.ceil(1 + this.subTerminals.length / 2);

    /* 
        let startIndex = this.mainTerminal.tagIndex;
 
       this.subTerminals.forEach((subTerminal) => {
          const x = subTerminal.tagIndex;
          if (x > endIndex) {
            endIndex = x;
          }
        }); 
            return endIndex - startIndex + 1;
        */
  }

  get parentTerminalStrip(): TerminalStrip | undefined {
    return this.mainTerminal.parent as TerminalStrip;
  }

  get mainConsumer(): Consumer | undefined {
    return this.mainTerminal.consumer;
  }

  get cableType(): CableType | undefined {
    return this.mainConsumer?.cableType;
  }

  set cableType(newValue: CableType | undefined) {
    if (this.mainConsumer) {
      this.mainConsumer.cableType = newValue;
      if (newValue) {
        this.assignSizeFromCableType();
      }

      (this.mainTerminal.parent as TerminalStrip)?.renumber(false);
    }
  }

  get isCollapsed() {
    return this.size > 1 && this.subTerminals.length == 0;
  }
  //
  // 0 = mainTerminal
  // 1..n = subTerminals
  //
  indexOfTerminal(terminal: Terminal): number | undefined {
    if (terminal === this.mainTerminal) {
      return 0;
    }

    for (let i = 0; i < this.subTerminals.length; i++) {
      const subTerminal = this.subTerminals[i];
      if (subTerminal === terminal) {
        return i + 1;
      }
    }

    return undefined;
  }

  allViaConsumersOfThisTeminalBlock(): Array<Consumer> {
    return this.mainTerminal.consumer?.getAllViaConsumers() || [];
  }

  assignSizeFromCableType() {
    const consumer = this.mainTerminal.consumer;
    const cableType = consumer?.cableType;

    if (cableType) {
      this.assignSizeFromCableSizeWithoutPEandN(cableType.numberOfWiresWithoutPEandN);
    }
  }

  assignSizeFromCableSizeWithoutPEandN(sizeWithoutPEandN: number) {
    if (!Number.isFinite(sizeWithoutPEandN)) return;

    // Ein Klemmblock besteht immer aus mindestens einer physischen Klemme.
    // Das gilt auch für "kein Kabel" und unvollständige ältere Projektdaten.
    const neededSize = Math.max(1, Math.ceil((Math.max(0, sizeWithoutPEandN) + 1) / 2));
    this.adjustSize(neededSize);
  }

  splitGroupTerminalIntoSingleTerminals() {
    const xsize = this.size;
    if (xsize > 1 && this.subTerminals.length == 0) {
      this.createSubTerminals(this.mainTerminal.tagIndex + 1, xsize - 1);
      this.mainTerminal.tag = `${this.mainTerminal.tagIndex}`;
    }
  }

  createSubTerminals(start: number, count: number) {
    for (let i = start; i < start + count; i++) {
      const t1 = this.mainTerminal.insertTerminal(`${i}a`, true, true); // no itx
      const t2 = this.mainTerminal.insertTerminal(`${i}b`, true, true); // no itx

      if (t1) this.subTerminals.push(t1);
      if (t2) this.subTerminals.push(t2);
    }
  }

  increaseNumberOfSubTerminals(count: number) {
    const start = this.mainTerminal.tagIndex + Math.ceil(this.subTerminals.length / 2);

    for (let i = 0; i < count; i++) {
      const t1 = this.mainTerminal.insertTerminal(`${start}z-${i}`, true, true); // no itx
      if (t1) this.subTerminals.push(t1);
    }
  }

  adjustSize(neededSize: number) {
    if (!Number.isFinite(neededSize)) return;
    neededSize = Math.max(1, Math.ceil(neededSize));

    if (neededSize == this.size) {
      return;
    }

    if (this.subTerminals.length == 0) {
      //
      // der block hat seine SubTerminals noch nicht herausgesplittet
      // daher müssen wir ausschließlich die size im Tag anpassen
      //
      this.mainTerminal.tagSize = neededSize;
    } else {
      //
      // der block hat seine SubTerminals noch BEREITS herausgesplittet
      // daher die SubTerminals anpassen !!!!!
      //

      if (neededSize > this.size) {
        //
        // die Anzahl der Kabel-Adern hat sich erhöht
        // wir müssen Klemmen hinzufügen
        //
        let start = 0;
        this.subTerminals.forEach((subTerminal) => {
          start = Math.max(start, subTerminal.tagIndex);
        });

        start++;

        const count = neededSize - this.size;
        this.increaseNumberOfSubTerminals(count * 2);
      } else if (neededSize < this.size) {
        for (let i = (neededSize - 1) * 2; i < this.subTerminals.length; i++) {
          const subTerminal = this.subTerminals[i];
          if (subTerminal && subTerminal.consumer) {
            return;
          }
        }

        while (this.subTerminals.length > 0 && Math.ceil(this.subTerminals.length / 2) > neededSize - 1) {
          const subTerminal = this.subTerminals.pop();
          if (subTerminal) {
            (subTerminal.parent as TerminalStrip).terminals.remove(subTerminal);
            subTerminal.delete();
          }
        }
      }
    }
    // renumber the added or removed subterminals will be children from the right mainTerminal
    // warum dauerschleife??????
    // this.parentTerminalStrip?.renumber(false);
  }

  assignNewTagIndex(tagIndex: number) {
    const s = this.size;

    this.mainTerminal.tagIndex = tagIndex;
    if (this.subTerminals.length == 0) {
      this.mainTerminal.tagSize = s;
    } else {
      for (let i = 0; i < this.subTerminals.length; i++) {
        const subTerminal = this.subTerminals[i];
        if (i % 2 == 0) {
          subTerminal.tag = `${tagIndex + 1 + Math.floor(i / 2)}a`;
        } else {
          subTerminal.tag = `${tagIndex + 1 + Math.floor(i / 2)}b`;
        }

        subTerminal.raiseOnChange("tag");
      }
    }

    this.mainTerminal.raiseOnChange("tag");
  }

  appendNewTerminalBlock(terminal: Terminal, terminalStrip: TerminalStrip, insertAfter: boolean) {
    const terminals = terminalStrip.terminals.sortBy("tag");

    const terminalBlocks = terminalStrip.getTerminalBlocks();

    const terminalBlockIndex = terminalBlocks.findIndex(
      (tb) => tb.mainTerminal == terminal || tb.subTerminals.includes(terminal),
    );
    const currentTerminalBlock = terminalBlocks[terminalBlockIndex];

    const newTerminal = new Terminal({}, terminalStrip);
    terminalStrip.terminals.add(newTerminal);

    const newTerminalBlock = new TerminalBlock(newTerminal, []);

    if (insertAfter) {
      newTerminal.tagIndex = this.mainTerminal.tagIndex + this.size - 1;
      newTerminal.tagSize = currentTerminalBlock.size;
      terminalBlocks.splice(terminalBlockIndex + 1, 0, newTerminalBlock);
    } else {
      newTerminal.tagIndex = this.mainTerminal.tagIndex;
      newTerminal.tagSize = currentTerminalBlock.size;

      terminal.tagIndex = this.mainTerminal.tagIndex + this.size - 1;
      terminalBlocks.splice(terminalBlockIndex, 0, newTerminalBlock);
    }

    terminalStrip.renumberTermialBlocks(terminalBlocks);
  }

  get hasNoConnectedConsumers(): boolean {
    if (this.mainTerminal.consumer) {
      return false;
    }
    for (const subTerminal of this.subTerminals) {
      if (subTerminal.consumer) {
        return false;
      }
    }
    return true;
  }

  delete() {
    this.parentTerminalStrip?.terminals.remove(this.mainTerminal);
    this.mainTerminal.delete();

    for (const subTerminal of this.subTerminals) {
      this.parentTerminalStrip?.terminals.remove(subTerminal);
      subTerminal.delete();
    }
    return true;
  }

  checkElcbUsage() {
    const terminals = [this.mainTerminal, ...this.subTerminals];

    let elcb: ELCB | undefined = this.mainTerminal.elcb;
    let elcbTerminal: Terminal | undefined = this.mainTerminal;

    for (const terminal of terminals) {
      if (terminal.elcb) {
        const conflict = elcb != undefined && terminal.elcb != elcb;

        const s = `Klemmen ${terminal.tag} / ${elcbTerminal?.tag || ""} haben unterschiedliche FIs`;

        terminal.flagErrors({ conflictingElcbs: s }, conflict);
        elcbTerminal?.flagErrors({ conflictingElcbs: s }, conflict);

        elcb = terminal.elcb;
        elcbTerminal = terminal;
      }
    }
  }

  //
  // result:
  //   0 = mainTerminal
  //   1..n = subTerminals [0..n-1  ]
  //
  // terminalViaWireIndex:
  //  mit PE:
  //     0 = PE
  //     1 = N
  //     2 = L1 braun (MainTerminal: 0)
  //     3 = L2 (SubTerminal[0]: 1)
  //     4 = L3 (SubTerminal[1]: 2)
  //
  // ohne PE:
  //     0 = N
  //     1 = L1 braun (MainTerminal: 0)
  //     2 = L2 (SubTerminal[0]: 1)
  //     3 = L3 (SubTerminal 2)

  indexOfTerminalfromWireIndex(terminalViaWireIndex: number): number {
    const cableType = this.cableType;

    if (cableType && !cableType.hasGround) {
      return terminalViaWireIndex - 1;
    } else {
      return terminalViaWireIndex - 2;
    }
  }

  terminalFromIndex(index: number): Terminal | undefined {
    if (index == 0) {
      return this.mainTerminal;
    } else {
      return this.subTerminals[index - 1];
    }
  }

  terminalFromWireIndex(terminalViaWireIndex: number): Terminal | undefined {
    const index = this.indexOfTerminalfromWireIndex(terminalViaWireIndex);
    return this.terminalFromIndex(index);
  }
}
